"use client";

import type { ReactNode } from "react";
import type { BuilderStudioDevice } from "./types";

export type BuilderDeviceControlLabels = Record<
  BuilderStudioDevice,
  string
>;

export type BuilderDeviceControlsProps = {
  device: BuilderStudioDevice;
  disabled?: boolean;
  labels: BuilderDeviceControlLabels;
  onDeviceChange: (device: BuilderStudioDevice) => void;
};

function DeviceIcon({
  device,
}: {
  device: BuilderStudioDevice;
}) {
  const paths: Record<BuilderStudioDevice, ReactNode> = {
    desktop: (
      <>
        <rect x="3" y="4" width="18" height="13" rx="2" />
        <path d="M8 21h8M12 17v4" />
      </>
    ),
    tablet: (
      <>
        <rect x="5" y="2" width="14" height="20" rx="2" />
        <path d="M11 18h2" />
      </>
    ),
    mobile: (
      <>
        <rect x="7" y="2" width="10" height="20" rx="2" />
        <path d="M11 18h2" />
      </>
    ),
  };

  return (
    <svg
      aria-hidden="true"
      className="h-[17px] w-[17px]"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.8"
    >
      {paths[device]}
    </svg>
  );
}

export function BuilderDeviceControls({
  device,
  disabled = false,
  labels,
  onDeviceChange,
}: BuilderDeviceControlsProps) {
  return (
    <div
      className="hidden items-center gap-1 rounded-lg border border-border bg-bg-elevated p-0.5 md:flex"
      data-builder-device-controls="true"
    >
      {(["desktop", "tablet", "mobile"] as const).map((option) => (
        <button
          type="button"
          className={[
            "inline-grid h-8 w-8 place-items-center rounded-md text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60",
            device === option
              ? "bg-primary/15 text-primary"
              : "text-text-muted hover:bg-surface-hover hover:text-text",
            disabled ? "cursor-not-allowed opacity-45" : "",
          ].join(" ")}
          aria-label={labels[option]}
          aria-pressed={device === option}
          disabled={disabled}
          key={option}
          title={labels[option]}
          onClick={() => onDeviceChange(option)}
        >
          <DeviceIcon device={option} />
        </button>
      ))}
    </div>
  );
}
